import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  EyeIcon,
  MicrophoneIcon,
  PlayIcon,
  SpeakerWaveIcon,
} from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';
import { buildPracticeLaunchUrl, isPracticeStartDisabled } from '../lib/practiceAssignments';
import { buildPracticeHistoryPath, getPracticeCardActions, practiceScoreLabel } from '../lib/practiceAttempts';
import { buildIeltsSpeakingLaunchUrl, formatIeltsBand, ieltsBandFromAttempt } from '../lib/ieltsSpeaking';

const authHeaders = (json = false) => ({
  ...(json ? { 'Content-Type': 'application/json' } : {}),
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const labels = {
  dictation: 'Dictation Loop',
  meaning_sprint: '7-Second Meaning',
  sudden_sprint: 'Lightning Challenge',
  pronunciation: 'Pronunciation Practice',
  adaptive_reading: 'Adaptive Reading',
  ielts_speaking: 'IELTS Speaking Practice',
};

const formatDate = (value, locale) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(locale === 'zh-CN' ? 'zh-CN' : 'en', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

export default function StudentPracticeListPage() {
  const { locale, t } = useI18n();
  const navigate = useNavigate();
  const [practices, setPractices] = useState([]);
  const [autonomousCount, setAutonomousCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = authHeaders();
      const [practiceResponse, selfCheckResponse, ieltsHistoryResponse] = await Promise.all([
        fetch('/api/student/practice-assignments', { headers }),
        fetch('/api/student/self-check/sessions?limit=30', { headers }),
        fetch('/api/student/ielts-speaking/history?limit=30', { headers }),
      ]);
      const practiceData = await practiceResponse.json().catch(() => ({}));
      if (!practiceResponse.ok) throw new Error(practiceData.message || t('Could not load your assigned practice.'));
      setPractices(Array.isArray(practiceData.practices) ? practiceData.practices : []);
      const history = await selfCheckResponse.json().catch(() => ({}));
      const ieltsHistory = await ieltsHistoryResponse.json().catch(() => ({}));
      setAutonomousCount(
        (Array.isArray(history.sessions) ? history.sessions.length : 0)
        + (Array.isArray(ieltsHistory.attempts) ? ieltsHistory.attempts.length : Array.isArray(ieltsHistory.history) ? ieltsHistory.history.length : 0),
      );
    } catch (loadError) {
      setError(loadError.message || t('Could not load your assigned practice.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const sections = useMemo(() => ({
    todo: practices.filter((item) => !['completed', 'overdue', 'upcoming'].includes(item.status)),
    upcoming: practices.filter((item) => item.status === 'upcoming'),
    completed: practices.filter((item) => item.status === 'completed'),
    overdue: practices.filter((item) => item.status === 'overdue'),
  }), [practices]);

  const start = async (item) => {
    const { assignmentId } = getPracticeCardActions(item);
    const id = assignmentId;
    if (!id || starting || isPracticeStartDisabled(item.status)) return;
    if (item.assignment?.practice_type === 'ielts_speaking') {
      navigate(buildIeltsSpeakingLaunchUrl({
        payload: { assignment_id: id, target_id: item.id || '' },
      }));
      return;
    }
    setStarting(id);
    setError('');
    try {
      const response = await fetch(`/api/student/practice-assignments/${id}/start`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = data.code === 'practice.not_available_yet'
          ? t('This practice opens later.')
          : data.message || t('This practice is not ready yet.');
        throw new Error(message);
      }
      navigate(buildPracticeLaunchUrl(data.launch));
    } catch (startError) {
      setError(startError.message || t('The practice could not be opened.'));
    } finally {
      setStarting('');
    }
  };

  const renderCard = (item) => {
    const assignment = item.assignment || {};
    const actions = getPracticeCardActions(item);
    const latest = item.latest_attempt || {};
    const isCompleted = item.status === 'completed';
    const icon = assignment.practice_type === 'pronunciation'
      ? <SpeakerWaveIcon className="h-5 w-5" />
      : assignment.practice_type === 'adaptive_reading'
        ? <ClockIcon className="h-5 w-5" />
        : assignment.practice_type === 'ielts_speaking'
          ? <MicrophoneIcon className="h-5 w-5" />
        : <PlayIcon className="h-5 w-5" />;
    const isIelts = assignment.practice_type === 'ielts_speaking';
    const ieltsBand = isIelts ? ieltsBandFromAttempt(latest) : null;
    const targetBand = isIelts ? assignment.config?.target_band : null;
    return (
      <li key={item.id} className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-indigo-700">
              {icon}{t(labels[assignment.practice_type] || 'Practice')}
            </div>
            <h3 data-i18n-skip className="mt-1 truncate text-lg font-bold text-gray-900">{assignment.title}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {isIelts
                ? (assignment.config?.question ? t('Prompt provided') : t('General speaking assessment'))
                : t('{count} exact meanings', { count: assignment.source?.items_snapshot?.length || 0 })}
              {isIelts && targetBand != null ? ` · ${t('Target band {band}', { band: formatIeltsBand(targetBand) })}` : ''}
              {assignment.config?.due_at ? ` · ${t('Due {date}', { date: formatDate(assignment.config.due_at, locale) })}` : ''}
            </p>
          </div>
          {isCompleted && <CheckCircleIcon className="h-6 w-6 shrink-0 text-emerald-600" />}
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
          <span className="inline-flex items-center gap-1.5 text-gray-500">
            <ClockIcon className="h-4 w-4" />
            {item.status === 'in_progress' ? t('In progress') : item.status === 'overdue' ? t('Overdue') : item.status === 'upcoming' ? t('Upcoming') : isCompleted ? t('Completed') : t('Not started')}
          </span>
          {isIelts
            ? ieltsBand != null && (
              <span className={`font-semibold ${item.status_reason === 'target_met' ? 'text-emerald-700' : item.status_reason === 'target_not_met' ? 'text-amber-700' : 'text-indigo-800'}`}>
                {t('Predicted band')}: <span data-i18n-skip>{practiceScoreLabel(latest, 'ielts_speaking')}</span>
                {item.status_reason === 'target_met' ? ` · ${t('Target reached')}` : item.status_reason === 'target_not_met' ? ` · ${t('Target not reached')}` : ''}
              </span>
            )
            : latest.accuracy != null && <span className="font-semibold text-gray-700">{Math.round(Number(latest.accuracy) * 100)}%</span>}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {actions.canViewAllAttempts && (
              <Link
                to={isIelts
                  ? buildIeltsSpeakingLaunchUrl({ payload: { assignment_id: actions.assignmentId, target_id: item.id || '', history: '1' } })
                  : buildPracticeHistoryPath(actions.assignmentId)}
                className="inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 font-semibold text-gray-800 hover:bg-gray-50"
              >
                <EyeIcon className="h-4 w-4" aria-hidden="true" />
                {t('View all attempts')}
              </Link>
            )}
            <button
              type="button"
              onClick={() => start(item)}
              disabled={starting === assignment.id || isPracticeStartDisabled(item.status)}
              className="inline-flex items-center gap-2 rounded bg-indigo-600 px-4 py-2 font-semibold text-white hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-50"
            >
              {starting === assignment.id ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlayIcon className="h-4 w-4" />}
              {isCompleted ? t('Practice again') : item.status === 'in_progress' ? t('Continue') : t('Start practice')}
            </button>
          </div>
        </div>
      </li>
    );
  };

  if (loading) return <p className="p-8 text-center text-gray-600">{t('Loading practice...')}</p>;
  return (
    <main className="mx-auto max-w-5xl p-4 md:p-8">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">{t('Practice')}</div>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">{t('My practices')}</h1>
          <p className="mt-2 text-gray-600">{t('Teacher assignments and independent practice stay together here.')}</p>
        </div>
        <button type="button" onClick={load} className="rounded border border-gray-300 p-2 text-gray-600 hover:bg-white" title={t('Refresh')}><ArrowPathIcon className="h-5 w-5" /></button>
      </header>
      {error && <div className="mb-5 rounded border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800">{error}</div>}
      {sections.todo.length > 0 && <section><h2 className="text-xl font-bold text-gray-800">{t('To do')}</h2><ul className="mt-4 grid gap-4 md:grid-cols-2">{sections.todo.map(renderCard)}</ul></section>}
      {sections.upcoming.length > 0 && <section className="mt-8"><h2 className="text-xl font-bold text-gray-800">{t('Upcoming')}</h2><ul className="mt-4 grid gap-4 md:grid-cols-2">{sections.upcoming.map(renderCard)}</ul></section>}
      {sections.overdue.length > 0 && <section className="mt-8"><h2 className="text-xl font-bold text-rose-800">{t('Overdue')}</h2><ul className="mt-4 grid gap-4 md:grid-cols-2">{sections.overdue.map(renderCard)}</ul></section>}
      <section className="mt-8">
        <h2 className="text-xl font-bold text-gray-800">{t('Completed')}</h2>
        {sections.completed.length > 0 ? <ul className="mt-4 grid gap-4 md:grid-cols-2">{sections.completed.map(renderCard)}</ul> : <div className="mt-4 rounded border border-gray-200 bg-gray-50 p-6 text-center text-gray-500">{t('Completed practice will appear here.')}</div>}
      </section>
      <section className="mt-8 rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
        {t('{count} independent practice records are available in your history.', { count: autonomousCount })}
      </section>
    </main>
  );
}
